import { useLocation, Navigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { PawPrint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, getDefaultRouteForRole } from "@/contexts/AuthContext";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";

export default function Login() {
  const { user } = useAuth();
  const location = useLocation();
  const error = location.state?.error;

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  if (user) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-amtm-bg flex flex-col items-center justify-center px-6 py-12">
      <Link to="/" className="mb-8">
        <img src={LOGO_URL} alt="AMTM" className="h-16 w-auto object-contain" />
      </Link>
      <div className="w-full max-w-sm bg-amtm-surface border border-amtm-border rounded-2xl shadow-md p-8">
        <div className="flex items-center gap-2 mb-6">
          <PawPrint className="h-6 w-6 text-amtm-teal" />
          <h1 className="font-display text-2xl text-amtm-text">Staff & Admin Login</h1>
        </div>
        <p className="text-sm text-amtm-textMuted mb-6">
          Sign in with the Google account your admin registered for you.
        </p>
        {error && (
          <p className="text-sm text-amtm-error mb-4" data-testid="login-error-message">
            {error}
          </p>
        )}
        <Button
          type="button"
          onClick={handleGoogleLogin}
          className="w-full h-12 bg-amtm-teal text-white hover:bg-amtm-tealHover font-semibold flex items-center justify-center gap-2"
          data-testid="google-login-button"
        >
          <svg className="h-5 w-5" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.084 5.571l6.19 5.238C40.205 35.836 44 30.48 44 24c0-1.341-.138-2.65-.389-3.917z" />
          </svg>
          Sign in with Google
        </Button>
      </div>
      <Link to="/" className="mt-6 text-sm text-amtm-textMuted hover:text-amtm-teal" data-testid="login-back-to-public-link">
        ← Back to public site
      </Link>
    </div>
  );
}
